import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { makeRequest } from '../../core/services/v1/request'
import Config from '../../core/config/'
import { showPairNameUsdt } from '../../core/helper/date-format'
import CoinLogo from './CoinLogo'
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Coins } from "lucide-react";

const OVERVIEW_REFRESH_MS = 30000
const MOVERS_SHOWN = 3

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
})

const fmtUsd = (n) => (Number.isFinite(n) ? `$${compact.format(n)}` : '--')
const fmtPct = (n) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : '--')

const fmtPrice = (p) => {
  const n = Number(p)
  if (!Number.isFinite(n) || n <= 0) return '--'
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (n >= 1) return String(Number(n.toFixed(4)))

  const s = n.toPrecision(4)
  return s.includes('e') ? n.toFixed(10).replace(/0+$/, '') : s
}

const SEGMENTS = [
  { from: 0, to: 25, color: '#f7525f', label: 'Extreme Fear' },
  { from: 25, to: 45, color: '#f68e4b', label: 'Fear' },
  { from: 45, to: 55, color: '#f7bf2f', label: 'Neutral' },
  { from: 55, to: 75, color: '#8ccb5e', label: 'Greed' },
  { from: 75, to: 100, color: '#20b26c', label: 'Extreme Greed' },
]

const getSegment = (v) =>
  SEGMENTS.find((s) => v >= s.from && v < s.to) || SEGMENTS[SEGMENTS.length - 1]

const CX = 110
const CY = 106
const R = 78
const GAP = 0.8
const TICK_STEP = 5

const pointAt = (value, r = R) => {
  const rad = Math.PI * (1 - value / 100)
  return [CX + r * Math.cos(rad), CY - r * Math.sin(rad)]
}

const arcPath = (from, to) => {
  const [x1, y1] = pointAt(from)
  const [x2, y2] = pointAt(to)
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`
}

const TICKS = Array.from({ length: 100 / TICK_STEP + 1 }, (_, i) => i * TICK_STEP)
const [START_X, START_Y] = pointAt(0)
const [END_X, END_Y] = pointAt(100)

const FearGreedCard = ({ value }) => {
  const [animated, setAnimated] = useState(0)

  useEffect(() => {
    if (!Number.isFinite(value)) return undefined
    const timer = setTimeout(() => setAnimated(value), 150)
    return () => clearTimeout(timer)
  }, [value])

  const hasValue = Number.isFinite(value)
  const active = hasValue ? getSegment(value) : null
  const needleDeg = (animated / 100) * 180
  const lastIndex = SEGMENTS.length - 1

  return (
    <div className='mk-card mk-widget'>
      <h3 className='mk-widget-title'>Fear &amp; Greed Index</h3>

      <div className='mk-widget-body'>
        <div className='mk-gauge'>
          <svg
            viewBox='0 8 220 110'
            className='mk-gauge-svg'
            role='img'
            aria-label={hasValue ? `Fear and greed index ${value}` : 'Fear and greed index'}
          >
            {TICKS.map((t) => {
              const major = t % 25 === 0
              const [x1, y1] = pointAt(t, R + 10)
              const [x2, y2] = pointAt(t, R + (major ? 18 : 14))
              return (
                <line
                  key={t}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={major ? '#9ca3af' : '#4b5563'}
                  strokeWidth={major ? 1.6 : 1.2}
                  strokeLinecap='round'
                />
              )
            })}

            {SEGMENTS.map((seg, i) => (
              <path
                key={seg.label}
                d={arcPath(
                  i === 0 ? seg.from : seg.from + GAP,
                  i === lastIndex ? seg.to : seg.to - GAP,
                )}
                fill='none'
                stroke={seg.color}
                strokeWidth='10'
              />
            ))}

            <circle cx={START_X} cy={START_Y} r='5' fill={SEGMENTS[0].color} />
            <circle cx={END_X} cy={END_Y} r='5' fill={SEGMENTS[lastIndex].color} />

            <g
              style={{
                transformOrigin: `${CX}px ${CY}px`,
                transformBox: 'view-box',
                transform: `rotate(${needleDeg}deg)`,
                transition: 'transform 1.3s cubic-bezier(0.2, 0.8, 0.2, 1)',
                opacity: hasValue ? 1 : 0,
              }}
            >
              <polygon
                points={`${CX - 68},${CY} ${CX},${CY - 3.5} ${CX},${CY + 3.5}`}
                fill='#e5e7eb'
              />
            </g>

            <circle cx={CX} cy={CY} r='8' fill='#0f1216' stroke='#e5e7eb' strokeWidth='2.5' />
            <circle cx={CX} cy={CY} r='2.5' fill={active ? active.color : '#6b7280'} />
          </svg>

          <div className='mk-gauge-info'>
            <div className='mk-gauge-value'>{hasValue ? value : '--'}</div>
            <div
              className='mk-gauge-label'
              style={{ color: active ? active.color : '#9ca3af' }}
            >
              {active ? active.label : 'Loading...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const StatsCard = ({ stats }) => {
  const rows = [
    {
      label: 'Market Cap',
      value: fmtUsd(stats?.marketCap),
      change: stats?.marketCapChange,
      icon: DollarSign
    },
    {
      label: '24h Volume',
      value: fmtUsd(stats?.volume),
      change: stats?.volumeChange,
      icon: BarChart3
    },
    {
      label: 'BTC Dominance',
      value: fmtPct(stats?.btcDominance),
      change: stats?.btcDominanceChange,
      icon: Coins
    },
  ]

  return (
    <div className='mk-card mk-widget mk-wide'>
      <h3 className='mk-widget-title'>Global Market</h3>

      <div className='mk-movers-list'>
        {rows.map((row) => {
          const hasChange = Number.isFinite(row.change)
          const up = hasChange && row.change >= 0
          const Icon = row.icon;
          return (
            <div
              key={row.label}
              className='mk-mover'
            >
              <Icon size={16} color='#1da094' />
              <span className='mk-mover-sym'>{row.label}</span>
              <span className='mk-mover-price'>{row.value}</span>
              <span className={`mk-mover-change ${hasChange ? (up ? 'mk-up' : 'mk-down') : ''}`}>
                {hasChange ? `${up ? '+' : ''}${row.change.toFixed(2)}%` : ''}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const PerformerCard = ({ title, coins, loading, onSelect }) => (
  <div className='mk-card mk-widget mk-wide'>
    <h3 className='mk-widget-title'>{title}</h3>

    <div className='mk-movers-list'>
      {loading &&
        Array.from({ length: MOVERS_SHOWN }).map((_, i) => (
          <div key={i} className='mk-skel mk-skel-row' />
        ))}

      {!loading && coins.length === 0 && (
        <p className='mk-muted mk-empty-note'>No data available</p>
      )}

      {!loading &&
        coins.slice(0, MOVERS_SHOWN).map((coin) => {
          const up = coin.change >= 0

          return (
            <div
              key={coin.symbol}
              className='mk-mover'
              role='button'
              tabIndex={0}
              onClick={() => onSelect(coin.symbol)}
              onKeyDown={(e) => e.key === 'Enter' && onSelect(coin.symbol)}
            >
              <CoinLogo src={coin.image} symbol={coin.base} size={26} />
              <span className='mk-mover-sym'>{coin.base}</span>
              <span className='mk-mover-price'>{fmtPrice(coin.price)}</span>
              <span className={`mk-mover-change ${up ? 'mk-up' : 'mk-down'}`}>
                {up ? '+' : ''}
                {coin.change.toFixed(2)}%
              </span>
            </div>
          )
        })}
    </div>
  </div>
)

const MarketOverview = () => {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true

    const load = async () => {
      try {
        const response = await makeRequest({
          url: `${Config.V3_API_URL}trade/getMarketOverview`,
          method: 'GET',
        })
        if (alive && response?.status) setData(response.data)
      } catch (err) {
        console.log('getMarketOverview error:', err)
      } finally {
        if (alive) setLoading(false)
      }
    }

    load()
    const interval = setInterval(() => {
      if (!document.hidden) load()
    }, OVERVIEW_REFRESH_MS)

    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [])

  const goTrade = (symbol) => navigate('/spot-usdt/' + showPairNameUsdt(symbol, '-'))

  return (
    <div className='mk-strip'>
      <FearGreedCard value={data?.fearGreed} />
      <StatsCard stats={data?.global} />
      <PerformerCard
        title='Top Performers'
        coins={data?.gainers || []}
        loading={loading}
        onSelect={goTrade}
      />
      <PerformerCard
        title='Low Performers'
        coins={data?.losers || []}
        loading={loading}
        onSelect={goTrade}
      />
    </div>
  )
}

export default MarketOverview